import { useState, useRef, useCallback, useEffect } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const HAS_SPEECH_API = !!SpeechRecognition;

export function useVoiceEngine({ sessionRef, isOnboardingRef, voiceEnabledRef, onUserMessage, onEchoMessage, onError }) {
  const [micActive, setMicActive] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [liveTranscript, setLiveTranscript] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [micPermission, setMicPermission] = useState("prompt");

  const micActiveRef = useRef(false);
  const isSendingRef = useRef(false);
  const currentAudioRef = useRef(null);
  const recognitionRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const accumulatedTextRef = useRef("");
  const persistentStreamRef = useRef(null);
  const persistentAnalyserRef = useRef(null);
  const persistentAudioCtxRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const wakeLockRef = useRef(null);

  useEffect(() => { micActiveRef.current = micActive; }, [micActive]);

  // Check mic permission on mount
  useEffect(() => {
    if (navigator.permissions) {
      navigator.permissions.query({ name: "microphone" }).then(result => {
        setMicPermission(result.state);
        result.onchange = () => setMicPermission(result.state);
      }).catch(() => { /* Permission API not supported */ });
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.abort(); } catch (e) { /* already stopped */ }
      }
      if (persistentStreamRef.current) {
        persistentStreamRef.current.getTracks().forEach(t => t.stop());
      }
      if (persistentAudioCtxRef.current?.state !== "closed") {
        persistentAudioCtxRef.current?.close().catch(() => { /* already closed */ });
      }
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => { /* already released */ });
      }
      clearTimeout(silenceTimerRef.current);
    };
  }, []);

  // ---- Mic Stream (persistent, for interrupt detection) ----

  const initMicStream = useCallback(async () => {
    if (persistentStreamRef.current?.active) return;
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
    });
    persistentStreamRef.current = stream;
    setMicPermission("granted");

    const ctx = new AudioContext();
    persistentAudioCtxRef.current = ctx;
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.3;
    source.connect(analyser);
    persistentAnalyserRef.current = analyser;
  }, []);

  const teardownMicStream = useCallback(() => {
    if (persistentStreamRef.current) {
      persistentStreamRef.current.getTracks().forEach(t => t.stop());
      persistentStreamRef.current = null;
    }
    if (persistentAudioCtxRef.current?.state !== "closed") {
      persistentAudioCtxRef.current?.close().catch(() => { /* already closed */ });
      persistentAudioCtxRef.current = null;
    }
    persistentAnalyserRef.current = null;
  }, []);

  // ---- Stop Speaking ----

  const stopSpeaking = useCallback(() => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
    }
    setIsSpeaking(false);
  }, []);

  // ---- Send voice text to backend ----

  const sendVoiceText = useCallback(async (text) => {
    if (!text.trim() || !sessionRef.current) return;
    isSendingRef.current = true;

    onUserMessage(text);
    setIsProcessing(true);

    try {
      const form = new FormData();
      form.append("text", text);
      form.append("session_id", sessionRef.current.id);
      form.append("is_onboarding", String(isOnboardingRef.current));
      form.append("voice_enabled", String(voiceEnabledRef.current));
      const res = await axios.post(`${API}/api/chat/respond`, form, { withCredentials: true });

      onEchoMessage(res.data.ai_text);
      setIsProcessing(false);

      if (res.data.audio && voiceEnabledRef.current) {
        playAudioInternal(res.data.audio);
      } else {
        restartListeningInternal();
      }
    } catch (err) {
      onError("Voice send failed");
      setIsProcessing(false);
      restartListeningInternal();
    } finally {
      isSendingRef.current = false;
    }
  }, [sessionRef, isOnboardingRef, voiceEnabledRef, onUserMessage, onEchoMessage, onError]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Restart listening (after TTS ends or error) ----

  const restartListeningInternal = useCallback(() => {
    if (!micActiveRef.current) return;
    setTimeout(() => {
      if (HAS_SPEECH_API) startListeningInternal();
      else startWhisperFallbackInternal();
    }, 300);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Play TTS audio with interrupt detection ----

  const playAudioInternal = useCallback((audioBase64) => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }

    const audio = new Audio(`data:audio/mpeg;base64,${audioBase64}`);
    currentAudioRef.current = audio;
    setIsSpeaking(true);

    const onEnd = () => {
      setIsSpeaking(false);
      currentAudioRef.current = null;
      restartListeningInternal();
    };
    audio.onended = onEnd;
    audio.onerror = onEnd;
    audio.play().catch(onEnd);

    // Voice interrupt monitor
    if (micActiveRef.current && persistentAnalyserRef.current) {
      const analyser = persistentAnalyserRef.current;
      const data = new Uint8Array(analyser.frequencyBinCount);
      let speechFrames = 0;

      const check = () => {
        if (!currentAudioRef.current) return;
        analyser.getByteFrequencyData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) sum += data[i] * data[i];
        const rms = Math.sqrt(sum / data.length);

        if (rms > 18) {
          speechFrames++;
          if (speechFrames > 6) {
            stopSpeaking();
            restartListeningInternal();
            return;
          }
        } else {
          speechFrames = Math.max(0, speechFrames - 1);
        }
        requestAnimationFrame(check);
      };
      requestAnimationFrame(check);
    }
  }, [stopSpeaking, restartListeningInternal]);

  // ---- Web Speech API (real-time transcription) ----

  const startListeningInternal = useCallback(() => {
    if (!HAS_SPEECH_API || isSendingRef.current) return;
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch (e) { /* already stopped */ }
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";
    recognition.maxAlternatives = 1;
    recognitionRef.current = recognition;
    accumulatedTextRef.current = "";

    recognition.onstart = () => {
      setIsListening(true);
      setLiveTranscript("");
    };

    recognition.onresult = (event) => {
      let finalText = "";
      let interimText = "";
      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) finalText += result[0].transcript;
        else interimText += result[0].transcript;
      }
      accumulatedTextRef.current = finalText;
      const display = (finalText + interimText).trim();
      setLiveTranscript(display);

      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = setTimeout(() => {
        const text = accumulatedTextRef.current.trim() || display;
        if (text) {
          try { recognition.stop(); } catch (e) { /* already stopped */ }
          setIsListening(false);
          setLiveTranscript("");
          sendVoiceText(text);
        }
      }, 2000);
    };

    recognition.onerror = (event) => {
      if (event.error === "no-speech") {
        setIsListening(false);
        if (micActiveRef.current && !isSendingRef.current) {
          setTimeout(() => startListeningInternal(), 300);
        }
        return;
      }
      if (event.error === "aborted") return;
      setIsListening(false);
      if (micActiveRef.current) startWhisperFallbackInternal();
    };

    recognition.onend = () => {
      setIsListening(false);
      const pending = accumulatedTextRef.current.trim();
      if (!pending && micActiveRef.current && !isSendingRef.current) {
        setTimeout(() => {
          if (micActiveRef.current && !isSendingRef.current) startListeningInternal();
        }, 300);
      }
    };

    try {
      recognition.start();
    } catch (err) {
      setIsListening(false);
    }
  }, [sendVoiceText]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Whisper fallback ----

  const startWhisperFallbackInternal = useCallback(async () => {
    if (isSendingRef.current) return;
    try {
      let stream = persistentStreamRef.current;
      if (!stream?.active) {
        await initMicStream();
        stream = persistentStreamRef.current;
      }

      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      let hasFired = false;
      mediaRecorder.onstop = async () => {
        if (hasFired) return;
        hasFired = true;
        const blob = new Blob(chunksRef.current, { type: mimeType });
        if (blob.size > 1000) {
          isSendingRef.current = true;
          setIsProcessing(true);
          try {
            const form = new FormData();
            form.append("audio", blob, "recording.webm");
            const res = await axios.post(`${API}/api/chat/transcribe`, form, { withCredentials: true });
            if (res.data.user_text?.trim()) {
              await sendVoiceText(res.data.user_text.trim());
            } else {
              isSendingRef.current = false;
              setIsProcessing(false);
              if (micActiveRef.current) startWhisperFallbackInternal();
            }
          } catch (err) {
            isSendingRef.current = false;
            setIsProcessing(false);
            if (micActiveRef.current) setTimeout(() => startWhisperFallbackInternal(), 500);
          }
        }
      };

      mediaRecorder.start(250);
      setIsListening(true);

      // Silence detection
      const analyser = persistentAnalyserRef.current;
      if (!analyser) return;
      const buf = analyser.frequencyBinCount;
      const data = new Uint8Array(buf);
      let silStart = null, hasSpeech = false, speechStart = null;

      const check = () => {
        if (!mediaRecorderRef.current || mediaRecorderRef.current.state !== "recording") return;
        analyser.getByteFrequencyData(data);
        let sum = 0;
        for (let i = 0; i < buf; i++) sum += data[i] * data[i];
        const rms = Math.sqrt(sum / buf);
        if (rms > 8) {
          silStart = null;
          if (!hasSpeech) { hasSpeech = true; speechStart = Date.now(); }
        } else if (hasSpeech && speechStart && Date.now() - speechStart > 400) {
          if (!silStart) silStart = Date.now();
          if (Date.now() - silStart > 2000) {
            if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop();
            setIsListening(false);
            return;
          }
        }
        requestAnimationFrame(check);
      };
      setTimeout(check, 200);
    } catch (err) {
      setIsListening(false);
    }
  }, [initMicStream, sendVoiceText]);

  // ---- Public: toggle mic ----

  const toggleMic = useCallback(async () => {
    if (navigator.vibrate) navigator.vibrate(30);

    if (micActive) {
      setMicActive(false);
      clearTimeout(silenceTimerRef.current);
      if (recognitionRef.current) {
        try { recognitionRef.current.abort(); } catch (e) { /* already stopped */ }
      }
      if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop();
      stopSpeaking();
      setIsListening(false);
      setLiveTranscript("");
      teardownMicStream();
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => { /* already released */ });
        wakeLockRef.current = null;
      }
    } else {
      setMicActive(true);
      try {
        if ('wakeLock' in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
        }
      } catch (e) { /* Wake Lock not supported */ }
      try {
        await initMicStream();
        if (HAS_SPEECH_API) startListeningInternal();
        else startWhisperFallbackInternal();
      } catch (err) {
        setMicActive(false);
        setMicPermission("denied");
      }
    }
  }, [micActive, initMicStream, teardownMicStream, stopSpeaking, startListeningInternal, startWhisperFallbackInternal]);

  // ---- Public: stop all ----

  const stopAll = useCallback(() => {
    setMicActive(false);
    clearTimeout(silenceTimerRef.current);
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch (e) { /* already stopped */ }
    }
    if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop();
    stopSpeaking();
    setIsListening(false);
    setLiveTranscript("");
    teardownMicStream();
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => { /* already released */ });
      wakeLockRef.current = null;
    }
  }, [stopSpeaking, teardownMicStream]);

  return {
    micActive,
    isListening,
    liveTranscript,
    isProcessing,
    isSpeaking,
    micPermission,
    toggleMic,
    stopSpeaking,
    stopAll,
    setIsProcessing,
    playAudio: playAudioInternal,
  };
}
